
import {
  checkValueOnBlurInput,
  formatByCountry,
  formatInputByCountry,
  parseByCountry,
  storeDatatoSessionStorage
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { CargoController } from "@/app/ui/controllers/CargoController";
import { CargoDetailStt, DimensionData } from "@/domain/entities/Cargo";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import { Options, prop, Vue } from "vue-class-component";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "../../modules/cargo-module";
import {
  CalculateVolumeApiRequest,
  CalculateVolumeRequest
} from "@/data/payload/api/CargoApiRequest";
import { debounce } from "lodash";
import { FlagsPermissionCargoV2 } from "@/feature-flags/flags-permission-cargo-v2";

class Props {
  isDimensionChanged = prop<boolean>({
    type: Boolean,
    default: false,
    required: true
  });
  isValidationAdjustWeightProofPhotoEnable = prop<boolean>({
    type: Boolean,
    default: false,
    required: true
  });
  isSummaryOnly = prop<boolean>({
    type: Boolean,
    default: false,
    required: false
  });
  isEdit = prop<boolean>({
    type: Boolean,
    default: false
  });
  isErrorEditWeight = prop<boolean>({
    type: Boolean,
    default: false,
    required: false
  });
}

@Options({
  components: {
    Accordion,
    AccordionTab
  }
})
class WeightSummaryTimbangUlang extends Vue.with(Props) {
  buttonText = "Tutup";

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  get form() {
    return CargoController.form;
  }

  get detailData() {
    return CargoController.cargoDetailData;
  }

  get sttResult(): CargoDetailStt[] {
    return CargoController.sttResultFinal;
  }

  get totalStt() {
    return this.sttResult.reduce((prev, curr: any) => {
      if (curr?.sttDetails) return prev + curr.sttDetails.length
      return prev + 1
    }, 0)
  }

  get isLoadingCalculate() {
    return CargoController.isLoadingCalculateVolume;
  }

  get totalSttPieces() {
    return CargoController.totalSttPieces;
  }

  get cargoInputForm() {
    return CargoController.cargoInputForm;
  }

  set actualTotalSTTPieces(value: any) {
    this.form.actualTotalSTTPieces = value;
  }

  get totalNewCalculationGrossWeights() {
    return CargoController.totalNewCalculationGrossWeights;
  }

  get totalNewCalculationVolumeWeights() {
    return CargoController.totalNewCalculationVolumeWeights;
  }

  get chargeableWeight() {
    return this.sttResult.length
      ? Math.max(
          1,
          this.parseNumber(String(this.totalNewCalculationGrossWeights)),
          this.parseNumber(String(this.totalNewCalculationVolumeWeights))
        )
      : 0;
  }

  get totalPieces() {
    return CargoController.totalSttPieces;
  }

  get totalGrossWeights() {
    return CargoController.totalNewCalculationGrossWeights.toString() || "";
  }

  get totalVolumeWeights() {
    return CargoController.totalNewCalculationVolumeWeights.toString() || "";
  }

  get isWeightDiscrepancy() {
    if (this.isEdit) return this.isErrorEditWeight;
    return !!(
      this.isValidationAdjustWeightProofPhotoEnable &&
        CargoController.calculateVolumeData.isUploadProofPhoto
    );
  }

  get errorDisputeCargo() {
    if (CargoController.isBagOnlyExceedMaxDisputeWeight) {
      return {
        message: "Berat tidak bisa lebih dari timbang ulang",
        tooltip: "Berat tidak bisa diubah karena mengikuti hasil bagging."
      };
    }

    if (CargoController.isExceedMaxDisputeWeight) {
      return {
        message: `Selisih CW kargo maks. ${FlagsPermissionCargoV2.config_max_dispute_weight.getValue()} kg per STT`,
        tooltip: ""
      };
    }

    if (this.isWeightDiscrepancy) {
      return {
        message: "Berat kargo melebihi berat timbang ulang",
        tooltip: ""
      };
    }

    return { message: "", tooltip: "" };
  }

  get actualChargeableWeight() {
    return CargoController.calculateVolumeData.chargeableWeight;
  }

  get totalPcs() {
    return CargoController.form.actualTotalSTTPieces;
  }

  get isErrorGrossWeight() {
    const weight = this.parseNumber(this.form.actualTotalCargoGrossWeight);
    const isError = this.isPlaneCargo() && weight > 400;
    CargoController.setErrorMaxGrossWeight(isError);
    return isError;
  }

  get isErrorVolumeWeight() {
    const volume = this.parseNumber(this.form.actualTotalCargoVolumeWeight);
    const isError = this.isPlaneCargo() && volume > 400;
    CargoController.setErrorMaxVolumeWeight(isError);
    return isError;
  }

  get isErrorMaxPieces() {
    const isError = this.isPlaneCargo() && this.totalPcs > 15;
    CargoController.setErrorMaxPieces(isError);
    return isError;
  }

  get actualTotalCargoGrossWeight() {
    return this.formatFloat(this.form.actualTotalCargoGrossWeight)
  }

  set actualTotalCargoGrossWeight(value: any) {
    this.form.actualTotalCargoGrossWeight = value;
  }

  private isPlaneCargo() {
    return this.form.cargoType === "plane";
  }

  parseNumber(value: string) {
    if (typeof value === "number") return value;
    return parseByCountry(value, { country: this.countryCode });
  }

  convertDecimal(value: string | number) {
    const num = typeof value === "string" ? parseByCountry(value, { country: this.countryCode }) : Number(value);
    return formatByCountry(num, { type: "weight", country: this.countryCode });
  }

  formatNoDecimal(value: string) {
    return formatInputByCountry(value, { type: "quantity", country: this.countryCode });
  }

  formatFloat(value: string) {
    return formatInputByCountry(value, { type: "weight", country: this.countryCode });
  }

  formatDimension(value: string) {
    return formatInputByCountry(value, { type: "dimension", country: this.countryCode });
  }

  calculateVolume = debounce((calculateType = "") => {
    CargoController.calculateVolume(new CalculateVolumeApiRequest({
      calculateType,
      calculateRequest: new CalculateVolumeRequest({
        cargoType: this.form.cargoType,
        chargeableWeightReweigh: this.chargeableWeight,
        currentGrossWeight: this.parseNumber(this.form.actualTotalCargoGrossWeight),
        height: this.form.actualDimension.height,
        length: this.form.actualDimension.length,
        width: this.form.actualDimension.width,
        totalPieces: this.form.actualTotalSTTPieces,
        volumeWeightReweigh: this.parseNumber(this.totalNewCalculationVolumeWeights as any),
        divider: getConfigDimensionDivider(this.form.cargoType),
        isMinimumVolumePerPiece: getConfigIsminimumVolumePerPiece(this.form.cargoType, this.form.cargoTypeStatus),
        minVolumePerPiece: getConfigMinimumVolumePerPiece(this.form.cargoType, this.form.cargoTypeStatus)
      })
    }))
  }, 250)

  async onChangeTotalCargoGrossWeight(value: any) {
    CargoController.setOnChangeTotalActualGrossWeight(value);
    await this.$nextTick();
    this.calculateVolume(CALCULATE_TYPE.CHANGE_GW);
    this.$emit("update:isEditFirstLanding", false);
  }

  formatValue(value: string, type: string) {
    if (checkValueOnBlurInput(value) === "withoutComma") {
      const newValue = formatByCountry(Number(value), { type: "weight", country: this.countryCode });
      if (type === "grossWeight") {
        CargoController.CargoInputData.totalActualCargoGrossWeight = newValue;
        this.form.actualTotalCargoGrossWeight = newValue;
        this.cargoInputForm.totalActualCargoGrossWeight = newValue;
      } else {
        this.form.actualTotalCargoVolumeWeight = newValue;
        this.cargoInputForm.totalActualCargoVolumeWeight = newValue;
      }
      storeDatatoSessionStorage("cargo-input-data", this.cargoInputForm);
    } else if (checkValueOnBlurInput(value) === "oneValueAfterComma") {
      if (type === "grossWeight") {
        CargoController.CargoInputData.totalActualCargoGrossWeight =
          value + "0";
        this.form.actualTotalCargoGrossWeight = value + "0";
        this.cargoInputForm.totalActualCargoGrossWeight = value + "0";
      } else {
        this.form.actualTotalCargoVolumeWeight = value + "0";
        this.cargoInputForm.totalActualCargoVolumeWeight = value + "0";
      }
      storeDatatoSessionStorage("cargo-input-data", this.cargoInputForm);
    } else if (checkValueOnBlurInput(value) === "minimumValue") {
      const minNum = Math.max(0.10, this.parseNumber(value));
      const newValue = formatByCountry(minNum, { type: "weight", country: this.countryCode });
      const sep = this.countryCode === "MY" ? "." : ",";
      const newValueMinimum = newValue.split(sep)[0];
      if (type === "grossWeight") {
        CargoController.CargoInputData.totalActualCargoGrossWeight = newValue;
        this.form.actualTotalCargoGrossWeight = newValue;
        this.cargoInputForm.totalActualCargoGrossWeight = newValue;
      } else {
        const zeroTen = this.countryCode === "MY" ? ".10" : ",10";
        this.form.actualTotalCargoVolumeWeight = newValueMinimum + zeroTen;
        this.cargoInputForm.totalActualCargoVolumeWeight =
          newValueMinimum + zeroTen;
      }
      storeDatatoSessionStorage("cargo-input-data", this.cargoInputForm);
    }
  }

  async onChangeTotalPieces(value: number) {
    CargoController.setOnChangeTotalColi(value);
    this.actualTotalSTTPieces = value;
    this.form.actualTotalSTTPieces = value;
    if (!value) return;
    await this.$nextTick();
    this.calculateVolume(CALCULATE_TYPE.CHANGE_PCS);
    this.$emit("update:isEditFirstLanding", false);
  }

  async onChangeDimension(value: DimensionData) {
    CargoController.setIsChangeVolumeActual(false);
    CargoController.setOnChangeDimension(value);
    await this.$nextTick();
    this.$emit("update:isDimensionChanged", true);
    this.$emit("update:isEditFirstLanding", false);
    CargoController.setIsDimensionChanged(true);
    this.calculateVolume(CALCULATE_TYPE.CHANGE_DIMENSION);
  }
}

export default WeightSummaryTimbangUlang;
